import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAccount, useConfig } from "wagmi";
import { getPublicClient } from "wagmi/actions";
import { formatUnits, type Address } from "viem";
import { REDES, type RedKeel } from "../config/networks";
import { useMiReferido, usePanelReferidor, useAceptarInvitacion, type InvitadoPanel, type FichaRed } from "../hooks/useReferidos";
import { enlaceInvitacion } from "../lib/referido";
import { corto, num } from "../lib/formato";
import { Giro } from "./Modal";
import { useT } from "../lib/i18n";

/**
 * Invitar y cobrar por ello.
 *
 * LO QUE ESTA PANTALLA TIENE QUE DEJAR CLARO EN LOS PRIMEROS DIEZ SEGUNDOS, porque es lo que decide
 * si alguien comparte su enlace o no:
 *
 *   1. A QUIEN INVITAS NO LE CUESTA NADA. Sigue pagando el mismo 10% de sus comisiones que pagaria
 *      si hubiera entrado solo. Lo que se reparte es la parte de Keellabs. Si esto no queda dicho,
 *      compartir el enlace se siente como venderle algo peor a un amigo, y nadie lo hace.
 *   2. NO SE COBRA DESDE EL PRIMER DIA. Hasta que el invitado no genera un minimo de comisiones, el
 *      referido no paga. Decirlo aqui, antes de que alguien mire su panel a cero y crea que esta
 *      roto, es la diferencia entre una regla y un fallo aparente.
 *   3. QUE SE COBRA SOLO. El reparto lo hace el contrato en el mismo cobro; no hay que reclamar
 *      nada, ni escribirnos, ni esperar a que alguien pague a mano.
 */
export function Referidos({ red, alVolver }: { red: RedKeel; alVolver: () => void }) {
  const t = useT();
  const { address } = useAccount();
  const { data: mio } = useMiReferido(red);
  const panel = usePanelReferidor();
  const [copiado, setCopiado] = useState(false);

  const enlace = address ? enlaceInvitacion(address) : "";
  const texto = t("Estoy usando Keellabs para poner a trabajar mi dinero en piscinas de liquidez. Si entras por aquí, entras igual que yo:");

  async function copiar() {
    try {
      await navigator.clipboard.writeText(enlace);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2500);
    } catch {
      // Sin permiso de portapapeles el campo sigue en pantalla y se puede seleccionar a mano.
    }
  }

  const abrir = (u: string) => window.open(u, "_blank", "noopener,noreferrer");

  return (
    <section className="tarjeta">
      <button className="btn fantasma" onClick={alVolver}>{t("← Volver")}</button>
      <div className="mod-titulo" style={{ marginTop: 8 }}>
        <span className="mod-ico" aria-hidden>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="8" width="18" height="4" rx="1" /><path d="M5 12v8a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-8" />
            <path d="M12 8v13" /><path d="M12 8S9.5 3 7 4.5 9 8 12 8zm0 0s2.5-5 5-3.5S15 8 12 8z" />
          </svg>
        </span>
        <h2 style={{ margin: 0 }}>{t("Invita y cobra")}</h2>
      </div>

      <p className="detalle">
        {t("Quien entre por tu enlace")} <strong>{t("paga exactamente lo mismo")}</strong> {t("que si hubiera entrado solo: el 10% de las comisiones que gane su piscina, nunca de su capital. Lo que se reparte es")}{" "}
        <strong>{t("nuestra")}</strong> {t("parte.")}
      </p>

      {/* EL REPARTO, EN CIFRAS Y NO EN UNA FRASE. Es el dato que decide si alguien comparte el
          enlace, y enterrado en un párrafo hay que leerlo dos veces para saber cuánto se lleva. */}
      <div className="reparto-ref">
        <div className="tuyo">
          <div className="n">2%</div>
          <div className="q">{t("para ti")}</div>
          <div className="s">{t("de las comisiones que genere tu invitado")}</div>
        </div>
        <div>
          <div className="n">8%</div>
          <div className="q">{t("para Keellabs")}</div>
          <div className="s">{t("lo que antes era el 10% entero")}</div>
        </div>
        <div>
          <div className="n">0</div>
          <div className="q">{t("le cuesta a él")}</div>
          <div className="s">{t("paga lo mismo, con invitación o sin ella")}</div>
        </div>
      </div>

      {/* EL ENLACE SE VE ENTERO. En un `input` de una línea se cortaba por la mitad y no había forma
          de comprobar a simple vista que la dirección del final es la tuya — que es justo lo único
          que hay que mirar antes de mandárselo a alguien. */}
      <div className="caja-enlace">
        <div className="et">{t("Tu enlace de invitación")}</div>
        <code className="enlace" onClick={(e) => {
          // Un clic selecciona el enlace entero: en el móvil, arrastrar para seleccionar texto
          // dentro de una tarjeta es justo lo que no se consigue a la primera.
          const r = document.createRange();
          r.selectNodeContents(e.currentTarget);
          const s = getSelection();
          s?.removeAllRanges();
          s?.addRange(r);
        }}>
          {enlace || t("conecta tu wallet para verlo")}
        </code>
        <div className="acciones">
          <button className="btn principal" onClick={copiar} disabled={!enlace}>
            {copiado ? t("Copiado ✓") : t("Copiar")}
          </button>
          <button className="btn wasap" disabled={!enlace}
            onClick={() => abrir(`https://wa.me/?text=${encodeURIComponent(`${texto} ${enlace}`)}`)}>
            WhatsApp
          </button>
          <button className="btn tele" disabled={!enlace}
            onClick={() => abrir(`https://t.me/share/url?url=${encodeURIComponent(enlace)}&text=${encodeURIComponent(texto)}`)}>
            Telegram
          </button>
        </div>
      </div>

      <p className="detalle">
        {t("Se cobra")} <strong>{t("solo")}</strong>{t(": cada vez que la piscina de tu invitado cobra comisiones, tu parte sale en esa misma transacción y llega a tu wallet. No hay nada que reclamar.")}{" "}
        <strong>{t("Eso sí")}</strong>{t(": no empieza el primer día. Tu invitado tiene que haber generado un mínimo de comisiones antes de que el reparto se active — es lo que evita que alguien abra dos wallets y se invite a sí mismo.")}
      </p>

      {!mio?.hayRegistro && (
        <div className="aviso">
          <p className="detalle">
            {t("En {etiqueta} el reparto", { etiqueta: red.etiqueta })} <strong>{t("todavía no está activo")}</strong>{t(": el registro no está desplegado en esta red. Tu enlace ya funciona y las altas quedan guardadas, así que quien entre ahora cuenta cuando se encienda.")}
          </p>
        </div>
      )}

      <h3 style={{ marginTop: 26 }}>{t("Lo que llevas ganado")}</h3>
      {panel.isLoading && <Giro texto={t("Contando…")} />}
      {panel.isError && (
        <div className="aviso"><p className="detalle">
          {t("No pude leer tus números ahora mismo — reinténtalo en un momento. Tu enlace y el reparto no dependen de esto.")}
        </p></div>
      )}
      {panel.data && (
        <>
          <div className="cifras">
            <div>
              <div className="k">{t("Invitados")}</div>
              <div className="v">{panel.data.invitados}</div>
              <div className="s">{t("wallets que entraron por tu enlace")}</div>
            </div>
            <div>
              <div className="k">{t("Registrados en cadena")}</div>
              <div className="v">{panel.data.registrados ?? "—"}</div>
              <div className="s">{t("los únicos que pueden llegar a pagarte")}</div>
            </div>
            <div>
              <div className="k">{t("Ya te pagan")}</div>
              <div className="v">{panel.data.activos}</div>
              <div className="s">{panel.data.invitados > 0
                ? t("de {n} · pasaron el mínimo", { n: panel.data.invitados })
                : t("cuando pasen el mínimo de comisiones")}</div>
            </div>
          </div>

          {panel.data.invitados === 0 ? (
            <p className="detalle ref-vacio">
              {t("Aún no ha entrado nadie por tu enlace. Compártelo 👆 — cobras el")} <strong>2%</strong> {t("de lo que generen sus piscinas, y a ellos no les cuesta nada.")}
            </p>
          ) : (
            <>
              {/* Primero lo que YA es tuyo (en verde, es la cifra que se viene a ver), luego la base
                  sobre la que se calcula. */}
              <Importes titulo={t("Ya cobrado · en tu wallet")} datos={panel.data.pagado} tono="cobrado"
                tokens={panel.data.tokens}
                vacio={t("Todavía nada cobrado. Se paga solo, en cuanto la piscina de un invitado activo cobre comisiones.")} />
              {/* LO QUE CUENTA PARA EL MÍNIMO, no nuestra comisión: es `generado` del registro —las
                  comisiones BRUTAS de sus piscinas, valoradas en el estable de cada red—. */}
              <Importes titulo={t("Comisiones brutas de tus invitados · cuentan para su mínimo")}
                datos={panel.data.generado} tokens={panel.data.tokens}
                vacio={t("Tus invitados registrados aún no han generado comisiones.")} />
              <p className="detalle" style={{ margin: "8px 0 0" }}>
                {t("Es lo que han ganado sus piscinas (antes de comisiones), medido en el estable de cada red. Cada invitado tiene que llegar al mínimo en una red para empezar a pagarte allí; a partir de ahí deja de sumarse aquí y tu parte llega sola.")}
              </p>
              <PorInvitado lista={panel.data.porInvitado} tokens={panel.data.tokens} />
            </>
          )}
        </>
      )}

      {mio?.referidorEnCadena && (
        <p className="detalle" style={{ marginTop: 20 }}>
          {t("A ti te invitó")} <code>{corto(mio.referidorEnCadena)}</code>
          {mio.activo ? t(" y ya está cobrando su parte de nuestra comisión.") : t(", y empezará a cobrar cuando tus piscinas hayan generado el mínimo.")}{" "}
          {t("A ti no te cuesta nada: pagas lo mismo que pagarías sin él.")}
        </p>
      )}
    </section>
  );
}

/**
 * El banner de "te han invitado". Vive donde se mira primero, no escondido en una pantalla aparte:
 * quien llega por un enlace no sabe todavia que existe la pantalla de referidos.
 *
 * PIDE UNA FIRMA, NO UNA TRANSACCION. Aceptar la invitacion no puede costarle gas a alguien que
 * acaba de llegar y todavia no ha puesto un euro.
 */
export function AvisoInvitacion({ red }: { red: RedKeel | undefined }) {
  const t = useT();
  const { data } = useMiReferido(red);
  const acc = useAceptarInvitacion(red);

  // Un alta firmada que el servicio guardó pero que NO llegó a la cadena (la firma caducó antes de
  // subirse) no cuenta para nada: hay que volver a firmar. Va antes que la invitación nueva porque
  // manda el primero que invitó, no el último enlace.
  if (data?.puedeRefirmar && data.referidorEnServicio) {
    return <AvisoRefirmar red={red} referidor={data.referidorEnServicio} faltan={data.faltan}
      registradoAqui={data.registradoAqui} acc={acc} />;
  }

  const yaTiene = !!(data?.referidorEnCadena || (data?.referidorEnServicio && !data.puedeRefirmar));
  if (!data?.pendiente || yaTiene) return null;

  if (acc.hecho) {
    return (
      <div className="aviso">
        <p className="detalle">
          {t("Invitación aceptada. A ti no te cambia nada: pagas la misma comisión de siempre.")}
        </p>
      </div>
    );
  }

  return (
    <div className="aviso">
      <p className="detalle">
        {t("Te invitó")} <code>{corto(data.pendiente)}</code>. {t("Si lo aceptas, se lleva una parte de")}{" "}
        <strong>{t("nuestra")}</strong> {t("comisión cuando tus piscinas ganen —")}{" "}
        <strong>{t("tú pagas exactamente lo mismo")}</strong>{t(", con invitación o sin ella.")}
      </p>
      {acc.error && <p className="detalle">{acc.error}</p>}
      <div className="acciones">
        <button className="btn principal" disabled={!!acc.haciendo}
          onClick={() => acc.aceptar(data.pendiente!)}>
          {acc.haciendo ? <Giro /> : t("Aceptar (solo una firma, sin gas)")}
        </button>
        <button className="btn fantasma" disabled={!!acc.haciendo} onClick={() => acc.rechazar()}>
          {t("No, gracias")}
        </button>
      </div>
    </div>
  );
}

/**
 * "Tu invitación no quedó registrada". Dos salidas: firmar otra vez (sin gas; el servicio la sube a
 * todas las redes mientras la firma vale) o `registrarPropio(referidor)` en la red actual pagando el
 * gas — que no depende de nada nuestro.
 */
function AvisoRefirmar({ red, referidor, faltan, registradoAqui, acc }: {
  red: RedKeel | undefined; referidor: Address; faltan: string[]; registradoAqui: boolean;
  acc: ReturnType<typeof useAceptarInvitacion>;
}) {
  const t = useT();
  const [oculto, setOculto] = useState(() => {
    try { return sessionStorage.getItem("keel.refirmar.oculto") === referidor; } catch { return false; }
  });
  if (oculto) return null;
  const nombres = faltan.map((k) => REDES[k as keyof typeof REDES]?.etiqueta ?? k).join(", ");

  if (acc.hecho) {
    return (
      <div className="aviso">
        <p className="detalle">
          {t("Hecho. Tu invitación se está registrando en la cadena; en un minuto debería verse como registrada.")}
        </p>
      </div>
    );
  }

  return (
    <div className="aviso">
      <p className="detalle">
        <strong>{t("Tu invitación de {quien} no quedó registrada.", { quien: corto(referidor) })}</strong>{" "}
        {t("Firma de nuevo (sin gas) y la registramos en la cadena. A ti no te cuesta nada ni cambia tu comisión.")}
      </p>
      {nombres && (
        <p className="detalle" style={{ marginTop: 4 }}>
          {t("Falta en: {redes}.", { redes: nombres })}
        </p>
      )}
      {acc.error && <p className="detalle">{acc.error}</p>}
      <div className="acciones">
        <button className="btn principal" disabled={!!acc.haciendo} onClick={() => acc.refirmar(referidor)}>
          {acc.haciendo === "firma" ? <Giro /> : t("Firmar de nuevo (sin gas)")}
        </button>
        {red?.referidos && !registradoAqui && (
          <button className="btn" disabled={!!acc.haciendo} onClick={() => acc.registrarYo(referidor)}
            title={t("Llama a registrarPropio en el registro de {red}", { red: red.etiqueta })}>
            {acc.haciendo === "tx" ? <Giro /> : t("Registrarlo yo mismo en {red} (pagas el gas)", { red: red.etiqueta })}
          </button>
        )}
        <button className="btn fantasma" disabled={!!acc.haciendo} onClick={() => {
          try { sessionStorage.setItem("keel.refirmar.oculto", referidor); } catch { /* sin storage */ }
          setOculto(true);
        }}>
          {t("Ahora no")}
        </button>
      </div>
    </div>
  );
}

type InfoTokens = Record<string, { simbolo: string; decimales: number }>;

/** Importe legible de un token de una red, o "…" si aún no se sabe su símbolo/decimales. */
function cantidad(tokens: InfoTokens | undefined, red: string, tok: string, crudo: string | null | undefined) {
  const i = tokens?.[`${red}:${tok}`];
  if (!i || crudo == null) return "…";
  return `${num(Number(formatUnits(BigInt(crudo), i.decimales)), 4)} ${i.simbolo}`;
}

const nombreRed = (k: string) => REDES[k as keyof typeof REDES]?.etiqueta.split(" · ").pop() ?? k;

/**
 * Desglose POR INVITADO con su WALLET y su estado REAL en cadena, red a red. El grafo de referidos
 * ya es público on-chain, así que mostrarte tus propios invitados aquí no expone nada nuevo.
 *
 *   · "no registrada en cadena": el registro no tiene su `referidorDe` apuntando a ti. Puede haber
 *     firmado y la firma caducó antes de subirse: no cuenta hasta que vuelva a firmar.
 *   · "registrada · acumulando": está en cadena; muestra `generado` frente a `minGenerado`.
 *   · "activa · te paga": pasó el mínimo en esa red y cada cobro suyo te manda tu parte.
 */
function PorInvitado({ lista, tokens }: { lista?: InvitadoPanel[]; tokens?: InfoTokens }) {
  const t = useT();
  if (!lista || !lista.length) return null;

  const corta = (w: string) => `${w.slice(0, 6)}…${w.slice(-4)}`;
  const copiar = (w: string) => { navigator.clipboard?.writeText(w).catch(() => {}); };

  const lineas = (inv: InvitadoPanel) => {
    const out: string[] = [];
    const entradas = Object.entries(inv.redes) as [string, FichaRed][];
    const reg = entradas.filter(([, f]) => f.estado === "acumulando" || f.estado === "activa");
    for (const [r, f] of reg) {
      if (f.estado === "activa") { out.push(`${nombreRed(r)}: ${t("activa · te paga")}`); continue; }
      const tok = f.estable;
      const g = f.generado?.[tok] ?? "0";
      const m = f.minimo?.[tok];
      const otros = Object.entries(f.generado ?? {}).filter(([k, v]) => k !== tok && v !== "0");
      if (g !== "0" || otros.length) {
        out.push(`${nombreRed(r)}: ${cantidad(tokens, r, tok, g)} ${t("de mínimo")} ${cantidad(tokens, r, tok, m)}`
          + otros.map(([k, v]) => ` · ${cantidad(tokens, r, k, v)}${f.minimo?.[k] ? ` / ${cantidad(tokens, r, k, f.minimo[k])}` : ""}`).join(""));
      }
    }
    const acum = reg.filter(([, f]) => f.estado === "acumulando" && (f.generado?.[f.estable] ?? "0") === "0");
    if (acum.length) {
      const [r0, f0] = acum[0]!;
      out.push(t("{n} red(es) sin comisiones aún (mínimo {m} por red)", {
        n: acum.length, m: cantidad(tokens, r0, f0.estable, f0.minimo?.[f0.estable]) }));
    }
    const otro = entradas.filter(([, f]) => f.estado === "otro_referidor").map(([r]) => nombreRed(r));
    if (otro.length) out.push(t("con otro referidor en: {redes}", { redes: otro.join(", ") }));
    const pag = Object.entries(inv.pagado ?? {}).flatMap(([r, pt]) =>
      Object.entries(pt).map(([tok, v]) => cantidad(tokens, r, tok, v)));
    if (pag.length) out.push(`${t("te ha pagado")}: ${pag.join(" + ")}`);
    return out;
  };

  const marca = (inv: InvitadoPanel) => {
    if (inv.activo) return { cls: "marca-ok", txt: t("activa · te paga") };
    if (inv.registrada) return { cls: "marca-pend", txt: t("registrada · acumulando") };
    const leidas = Object.values(inv.redes).some((f) => f.estado !== "sin_leer");
    if (!leidas) return { cls: "marca-pend", txt: t("comprobando…") };
    return { cls: "marca-no", txt: t("no registrada en cadena") };
  };

  const nota = (inv: InvitadoPanel) => {
    if (inv.registrada) {
      const total = Object.keys(inv.redes).length;
      const n = Object.values(inv.redes).filter((f) => f.estado === "acumulando" || f.estado === "activa").length;
      return n < total ? t("registrada en {n} de {total} redes", { n, total }) : null;
    }
    if (inv.estadoAlta === "subiendo") return t("registrándose ahora mismo…");
    if (inv.estadoAlta === "caducada_sin_subir" || inv.estadoAlta === "parcial_caducada")
      return t("su firma caducó antes de subirse: que abra keellabs.app con su wallet y firme de nuevo (sin gas)");
    return null;
  };

  return (
    <div className="ref-invitados" style={{ marginTop: 14 }}>
      <div className="et">{t("Tus invitados · {n} {palabra}", { n: lista.length, palabra: lista.length === 1 ? t("wallet") : t("wallets") })}</div>
      <ul>
        {lista.map((inv, n) => {
          const m = marca(inv);
          const ls = lineas(inv);
          const nt = nota(inv);
          return (
            <li key={inv.wallet ?? n} className="ref-inv">
              <span className="quien">
                <button
                  className="ref-wallet"
                  title={t("{wallet} · copiar", { wallet: inv.wallet })}
                  onClick={() => copiar(inv.wallet)}
                >
                  {corta(inv.wallet)}
                </button>
                <span className={m.cls}>{m.txt}</span>
              </span>
              {(ls.length > 0 || nt) && (
                <span className="cuanto">
                  {ls.map((l, i) => <span key={i} className="ref-linea">{l}</span>)}
                  {nt && <span className="ref-linea nota">{nt}</span>}
                </span>
              )}
            </li>
          );
        })}
      </ul>
      <p className="detalle" style={{ margin: "10px 0 0" }}>
        {t("Estado leído de la cadena, red a red. Solo cuentan las registradas; toca una wallet para copiarla.")}
      </p>
    </div>
  );
}

/**
 * Importes por red y por token. SE USAN EL SIMBOLO Y LOS DECIMALES DE CADA TOKEN (del servicio, o
 * leídos de la cadena si no vienen), no se asumen: un USDC de 6 decimales pintado como si tuviera
 * 18 enseña 0,000000012 donde hay 12 dólares.
 */
function Importes({ titulo, datos, tono, vacio, tokens }: {
  titulo: string; datos: Record<string, Record<string, string>>;
  tono?: "cobrado"; vacio?: string; tokens?: InfoTokens;
}) {
  const config = useConfig();
  const pares = Object.entries(datos ?? {}).flatMap(([red, porToken]) =>
    Object.entries(porToken).map(([token, importe]) => ({ red, token: token as Address, importe }))
  );

  const info = useQuery({
    queryKey: ["tokens-referidos", pares.map((p) => `${p.red}:${p.token}`).join(",")],
    enabled: pares.length > 0 && pares.some((p) => !tokens?.[`${p.red}:${p.token}`]),
    staleTime: 3_600_000,
    queryFn: async () => {
      const abi = [
        { type: "function", name: "symbol", stateMutability: "view", inputs: [], outputs: [{ type: "string" }] },
        { type: "function", name: "decimals", stateMutability: "view", inputs: [], outputs: [{ type: "uint8" }] },
      ] as const;
      const salida: Record<string, { simbolo: string; decimales: number }> = {};
      for (const p of pares) {
        const cadena = REDES[p.red as keyof typeof REDES]?.idCadena;
        if (!cadena) continue;
        const cliente = getPublicClient(config, { chainId: cadena });
        if (!cliente) continue;
        try {
          const [simbolo, decimales] = await cliente.multicall({
            allowFailure: false,
            contracts: [
              { address: p.token, abi, functionName: "symbol" },
              { address: p.token, abi, functionName: "decimals" },
            ],
          });
          salida[`${p.red}:${p.token}`] = { simbolo: simbolo as string, decimales: Number(decimales) };
        } catch {
          // Un token que no contesta se queda sin nombre, y entonces no se pinta: mas vale una
          // linea de menos que una cifra con las comas donde no van.
        }
      }
      return salida;
    },
  });

  // Estado vacío RESUELTO: en vez de no pintar nada (que deja al usuario sin saber si es 0 o si
  // falló), se muestra el título con una línea que explica por qué está a cero.
  if (!pares.length) {
    return (
      <div className="resumen-crear ref-importes" style={{ marginTop: 14 }}>
        <div><span className="k"><strong>{titulo}</strong></span><span className="v" /></div>
        {vacio && <div><span className="k" style={{ color: "var(--sonda)", fontWeight: 400 }}>{vacio}</span><span className="v" /></div>}
      </div>
    );
  }

  return (
    <div className={`resumen-crear ref-importes ${tono === "cobrado" ? "cobrado" : ""}`} style={{ marginTop: 14 }}>
      <div><span className="k"><strong>{titulo}</strong></span><span className="v" /></div>
      {pares.map((p) => {
        const i = tokens?.[`${p.red}:${p.token}`] ?? info.data?.[`${p.red}:${p.token}`];
        return (
          <div key={`${p.red}:${p.token}`}>
            <span className="k">{REDES[p.red as keyof typeof REDES]?.etiqueta ?? p.red}</span>
            <span className="v">
              {i ? `${num(Number(formatUnits(BigInt(p.importe), i.decimales)), 6)} ${i.simbolo}` : "…"}
            </span>
          </div>
        );
      })}
    </div>
  );
}
